/**
 * Excel-eksport for ledelsesdashbordet (issue #13276, krav 1.7.2).
 *
 * Bygger et arbeidshefte med flere ark:
 *  1. Sammendrag  – HMS-KPI-er (åpne avvik, lukketid, sykefravær)
 *  2. Sykefravær  – per år og kvartal (matcher NAV/SSB-metodikken)
 *  3. Avvik       – rådata for alle avvik (grunnlag for egne analyser)
 *
 * exceljs lastes via dynamisk import slik at biblioteket havner i en egen
 * chunk og ikke belaster hovedbundlen. UI-tekst hentes via i18n (t()).
 */
import { getStore } from 'src/store';
import type { State } from 'src/store/types';
import { getOrganization, issuesNotDeleted, isIssueProcessed } from 'src/store/selectors';
import { calculateSicknessAbsenceStats } from 'src/utilities/sickness-absence-stats-selector';
import { LocalDate } from 'src/utilities/local-date';
import { getKpiSummary } from 'src/models/pages/status-page-view';
import { t } from 'src/i18n';

function issueLabel(issue: { name?: string; description?: string }): string {
  const name = (issue.name ?? '').trim();
  if (name) return name;
  const description = (issue.description ?? '').trim();
  if (description) return description.length > 120 ? description.slice(0, 117) + '…' : description;
  return t('excelExport.untitled');
}

function fileNameSlug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9æøå]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'organisasjon'
  );
}

function triggerDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Frigjør minnet etter at nedlastingen har startet.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function percent(part: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((part / total) * 1000) / 10;
}

export async function exportStatusPageToExcel(): Promise<void> {
  const state: State = getStore().getState();
  const organization = getOrganization(state);
  const organizationName = organization?.name ?? t('excelExport.organization');
  const today = state.today;

  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  workbook.creator = 'TrinnVis';
  workbook.created = new Date();

  buildSummarySheet(workbook, state, organizationName, today);
  buildSicknessAbsenceSheet(workbook, state);
  buildIssuesSheet(workbook, state);

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(buffer as ArrayBuffer, `hms-eksport-${fileNameSlug(organizationName)}-${today}.xlsx`);
}

function buildSummarySheet(
  workbook: import('exceljs').Workbook,
  state: State,
  organizationName: string,
  today: string,
): void {
  const kpis = getKpiSummary(state);
  const sheet = workbook.addWorksheet(t('excelExport.summarySheet'));
  sheet.columns = [
    { header: t('excelExport.kpiHeader'), key: 'label', width: 42 },
    { header: t('excelExport.value'), key: 'value', width: 18 },
  ];
  sheet.getRow(1).font = { bold: true };

  sheet.addRow({ label: t('excelExport.organization'), value: organizationName });
  sheet.addRow({ label: t('excelExport.generated'), value: today });
  sheet.addRow({ label: '', value: '' });
  sheet.addRow({ label: t('status.openIssues'), value: kpis.openIssues });
  sheet.addRow({ label: t('excelExport.newIssues12m'), value: kpis.newIssuesLast12Months });
  sheet.addRow({
    label: t('excelExport.avgClosingDays'),
    value: kpis.averageClosingDays ?? t('excelExport.noClosedIssues'),
  });
  sheet.addRow({ label: t('excelExport.closedIssues12m'), value: kpis.closedIssuesLast12Months });

  const yearSuffix = kpis.sickLeaveYearName ? ` (${kpis.sickLeaveYearName})` : '';
  sheet.addRow({
    label: t('excelExport.sickLeaveExclLabel', { year: yearSuffix }),
    value: kpis.sickLeavePercent ?? t('excelExport.notAvailable'),
  });
  sheet.addRow({
    label: t('excelExport.sickLeaveInclLabel', { year: yearSuffix }),
    value: kpis.sickLeavePercentTotal ?? t('excelExport.notAvailable'),
  });
}

function buildSicknessAbsenceSheet(workbook: import('exceljs').Workbook, state: State): void {
  const sheet = workbook.addWorksheet(t('status.sickLeave'));
  sheet.columns = [
    { header: t('excelExport.period'), key: 'period', width: 16 },
    { header: t('excelExport.workDays'), key: 'workDays', width: 14 },
    { header: t('excelExport.absenceDaysExcl'), key: 'absenceDays', width: 30 },
    { header: t('excelExport.absenceDaysIncl'), key: 'absenceDaysTotal', width: 28 },
    { header: t('excelExport.sickLeavePctExcl'), key: 'percent', width: 20 },
    { header: t('excelExport.sickLeavePctIncl'), key: 'percentTotal', width: 20 },
  ];
  sheet.getRow(1).font = { bold: true };

  const stats = calculateSicknessAbsenceStats(state);
  if (!stats.genderDefined) {
    sheet.addRow({ period: t('excelExport.noGenderData') });
    return;
  }
  if (stats.years.length === 0) {
    sheet.addRow({ period: t('excelExport.noSickLeaveData') });
    return;
  }

  for (const year of stats.years) {
    const yearRow = sheet.addRow({
      period: year.yearName,
      workDays: Math.round(year.workDays),
      absenceDays: Math.round(year.absenceDays * 10) / 10,
      absenceDaysTotal: Math.round(year.absenceDaysTotal * 10) / 10,
      percent: percent(year.absenceDays, year.workDays),
      percentTotal: percent(year.absenceDaysTotal, year.workDays),
    });
    yearRow.font = { bold: true };
    for (const quarter of year.quarters) {
      sheet.addRow({
        period: `  ${quarter.quarterName}`,
        workDays: Math.round(quarter.workDays),
        absenceDays: Math.round(quarter.absenceDays * 10) / 10,
        absenceDaysTotal: Math.round(quarter.absenceDaysTotal * 10) / 10,
        percent: percent(quarter.absenceDays, quarter.workDays),
        percentTotal: percent(quarter.absenceDaysTotal, quarter.workDays),
      });
    }
  }
}

function buildIssuesSheet(workbook: import('exceljs').Workbook, state: State): void {
  const sheet = workbook.addWorksheet(t('status.issues'));
  sheet.columns = [
    { header: t('excelExport.issueTitle'), key: 'title', width: 50 },
    { header: t('excelExport.reportedDate'), key: 'reported', width: 14 },
    { header: t('excelExport.status'), key: 'status', width: 18 },
    { header: t('excelExport.processedDate'), key: 'processed', width: 16 },
    { header: t('excelExport.closingDaysHeader'), key: 'closingDays', width: 16 },
  ];
  sheet.getRow(1).font = { bold: true };

  const issues = [...issuesNotDeleted(state)].sort((a, b) =>
    (b.reportedDate ?? '').localeCompare(a.reportedDate ?? ''),
  );

  for (const issue of issues) {
    const processed = isIssueProcessed(issue);
    let closingDays: number | '' = '';
    if (processed && issue.reportedDate && issue.processedDate) {
      const days = LocalDate.fromString(issue.reportedDate).until(LocalDate.fromString(issue.processedDate));
      if (days >= 0) closingDays = days;
    }
    sheet.addRow({
      title: issueLabel(issue),
      reported: issue.reportedDate ?? '',
      status: processed ? t('excelExport.statusProcessed') : t('excelExport.statusActive'),
      processed: processed ? (issue.processedDate ?? '') : '',
      closingDays,
    });
  }

  if (issues.length === 0) {
    sheet.addRow({ title: t('excelExport.noIssues') });
  }
}
